import React, { useRef, useEffect, useContext } from 'react';
import { ReactiveJsonSubroot, GlobalDataContext } from '@ea-lab/reactive-json';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { formatArchiveDate } from '../../services/archive';
import { formatDueDate, isOverdue } from './kanbanRowValues';

// Heights are stored outside component instances so they survive
// unmount/remount when a card moves between columns during drag.
const cardHeightsByTaskId = new Map();

/**
 * Minimal DnD wrapper around a declarative KanbanCard.yaml subroot.
 *
 * Responsibilities:
 *  - Provide the useSortable hook (drag-and-drop wiring)
 *  - Compose the dataOverride so the card YAML has everything it needs
 *  - Render the card body via ReactiveJsonSubroot (all visual logic in YAML)
 *
 * Events dispatched *from within* the card YAML plugins reach KanbanBoard
 * via native CustomEvents (kanbanSubtaskDrilldown, kanbanDeleteTask), so no
 * callback props are required here.
 */
// React.memo: a column can render dozens of cards, each a reactive-json subroot.
// Without memoization, every optimistic setTasks during a drag re-renders ALL
// cards in every (non-memoized) column, which is the #2 cause of drag lag. Props
// here are stable references between renders (option arrays keep their identity,
// no callback props — card interactions ride the kanban* CustomEvent bus), so the
// default shallow compare is correct: only the card whose `task` object actually
// changed (the dragged one, re-minted by setTasks) re-renders.
const KanbanSortableWrapper = React.memo(({ task, users, tagOptions, returnTypeOptions, typeOptions, showDetails, subtaskCount, isRecentlyMoved }) => {
  const taskId = task.data.id;
  const isArchived = !!task.data.archived;
  // Désarchiver is admin / project-lead only. The real project role comes from
  // the page store (/api/project/{id}/role); the server re-checks anyway.
  const gdc = useContext(GlobalDataContext);
  const realUserRole = gdc?.data?.currentUserRoleData?.role || '';
  const canManageArchive = ['Administrateur', 'Chef de projet', 'CDP', 'project-admin', 'project-cdp'].includes(realUserRole);
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    id: taskId,
    data: { type: 'Task', task },
    disabled: isArchived,  // archived cards are read-only → not draggable
  });
  const cardRef = useRef(null);

  const style = { transition, transform: CSS.Translate.toString(transform) };

  // ResizeObserver captures the real height even after async YAML content loads
  // (the subroot doesn't trigger a re-render of this parent).
  useEffect(() => {
    const node = cardRef.current;
    if (!node || isDragging) return;
    const ro = new ResizeObserver(([entry]) => {
      if (entry.borderBoxSize?.[0]) {
        cardHeightsByTaskId.set(taskId, entry.borderBoxSize[0].blockSize);
      } else {
        cardHeightsByTaskId.set(taskId, node.offsetHeight);
      }
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, [isDragging, taskId]);

  if (isDragging) {
    const h = cardHeightsByTaskId.get(taskId) || 100;
    return (
      <div
        ref={setNodeRef}
        style={{ ...style, height: h }}
        className="bg-white rounded-lg opacity-30 border-2 border-iris-400 border-dashed w-full mb-3"
      />
    );
  }

  // Build the data context the card YAML will receive as ~~.
  //
  // apiData is a proper syncable object: DataSync (path: ~~.apiData) checks
  // apiData.submission_url to determine the save endpoint, and posts apiData.data
  // as the payload.  Only API-safe fields (no status/type labels) go into data.
  //
  // Plugins write to "apiData.data.priority", "apiData.data.tags" etc., which
  // are store paths that bubble up to notifiy the "apiData" DataSync subscription.
  //
  // The flat `data` key carries display-only info (type label, task id…) that
  // the YAML card reads via ~~.data.X but that is NOT sent by DataSync.
  const dataOverride = {
    apiData: {
      submission_url: task.submission_url,
      data: {
        title: task.data.title,
        priority: task.data.rating ?? 0,
        complexity: task.data.complexity ?? 0,
        tags: task.data.tags ?? [],
        isUrgent: task.data.isUrgent ?? false,
        estimation: task.data.estimation ?? 0,
        assignee: task.data.assignee || null,  // empty string → null (entity ref)
      },
    },
    // Display-only — not inside apiData.data, so never POSTed by DataSync.
    data: {
      id: task.data.id,
      type: task.data.type,
      state: task.data.state,  // T17: current workflow state (for the transition menu)
      title: task.data.title,
      // Description en texte riche : fournie à la carte UNIQUEMENT comme aperçu
      // en lecture seule (panneau du titre + œil « aperçu ») — jamais dans
      // apiData, donc jamais renvoyée par DataSync.
      description: task.data.description || '',
      demande_evolution: task.data.demande_evolution,
      assignee: task.data.assignee || null,
      isUrgent: task.data.isUrgent ?? false,
      // Échéance (field_due_date, date seule) : la carte n'affiche que le
      // libellé, et le booléen `dueDateOverdue` décide de la puce rouge. La
      // comparaison de dates est faite ICI, en JS : un `when/is` de
      // reactive-json ne compare que des valeurs déjà posées dans le store.
      dueDate: task.data.dueDate || '',
      dueDateLabel: formatDueDate(task.data.dueDate),
      dueDateOverdue: isOverdue(task.data.dueDate),
      archived: task.data.archived || null,  // archive timestamp → greyed/badge/read-only in the card
      isArchived: isArchived,                // boolean for the YAML hide/show actions
      archivedLabel: isArchived ? `Archivé le ${formatArchiveDate(task.data.archived)}` : '',
      canUnarchive: canManageArchive,        // gates the "Désarchiver" button
      // A card knows whether IT is archived, never whether its PROJECT is — yet
      // an archived project is read-only too (the board shows a banner saying
      // exactly that). Read from the same page store the board reads, so actions
      // in the card YAML can hide themselves instead of offering the impossible.
      projectArchived: !!gdc?.data?.project?.archived,
      // Compact-mode flag (eye toggle). MUST live inside `data`: action `when`
      // conditions only resolve under ~~.data.* in the card subroot — a root-level
      // key like ~~.showDetails evaluates to undefined in hide/show actions.
      showDetails: showDetails ?? true,
      // Contenus liés (entreprise, transaction, contact…) : type + titre + URL de
      // la fiche, joints par le back et le mappeur (cf. mapLinkedContent).
      linkedContentRefs: task.data.linkedContentRefs ?? [],
      // Identifiant lisible (« ORB-14 », module orbit_ref) : absent de la
      // réponse tant que le projet n'a pas de clé — chaîne vide = masqué.
      ref: task.data.ref ?? '',
    },
    delete_url: task.delete_url,
    users: users ?? [],
    tagOptions: tagOptions ?? [],
    returnTypeOptions: returnTypeOptions ?? [],
    typeOptions: typeOptions ?? [],
    subtaskCount: subtaskCount ?? 0,
  };

  return (
    <div
      ref={(node) => { setNodeRef(node); cardRef.current = node; }}
      style={style}
      {...attributes}
      {...(isArchived ? {} : listeners)}
      // isRecentlyMoved: transient "it landed here" marker after a drop into
      // another column (the board owns the 10s timer). A `ring` — drawn outside
      // the box — so the card never changes size or position. Transient
      // interaction state, like the drag ghost above: it belongs here, not in
      // KanbanCard.yaml, which describes the card's content.
      className={`mb-3 touch-none ${isArchived ? 'cursor-default opacity-60' : 'cursor-move'} ${
        isRecentlyMoved ? 'ring-2 ring-iris-300 rounded-xl' : ''}`}
    >
      {/* ReactiveJsonSubroot is a reactive-json plugin: it expects { props } not bare JSX props */}
      <ReactiveJsonSubroot
        props={{ rjOptions: { rjBuildUrl: '/components/KanbanCard.yaml', dataOverride } }}
      />
    </div>
  );
});
KanbanSortableWrapper.displayName = 'KanbanSortableWrapper';

export default KanbanSortableWrapper;
